import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Download, Building2, TrendingUp, AlertTriangle, DollarSign, Activity } from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell,
} from 'recharts';
import { useDepartmentCostReport, DepartmentCostFilters } from '@/hooks/finance/useDepartmentCostReport';
import * as XLSX from 'xlsx';

type TimeRange = 'this_month' | 'last_3_months' | 'last_6_months' | 'last_year' | 'custom';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#8dd1e1', '#a4de6c', '#d0ed57'];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-ET', {
    style: 'currency',
    currency: 'ETB',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

const formatAccounting = (amount: number) =>
  amount < 0
    ? `(${formatCurrency(Math.abs(amount))})`
    : formatCurrency(amount);

const DepartmentCostReport: React.FC = () => {
  const [timeRange, setTimeRange] = useState<TimeRange>('last_3_months');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');

  const getDateRange = () => {
    const now = new Date();
    switch (timeRange) {
      case 'this_month':
        return { dateFrom: format(startOfMonth(now), 'yyyy-MM-dd'), dateTo: format(endOfMonth(now), 'yyyy-MM-dd') };
      case 'last_3_months':
        return { dateFrom: format(subMonths(now, 3), 'yyyy-MM-dd'), dateTo: format(now, 'yyyy-MM-dd') };
      case 'last_6_months':
        return { dateFrom: format(subMonths(now, 6), 'yyyy-MM-dd'), dateTo: format(now, 'yyyy-MM-dd') };
      case 'last_year':
        return { dateFrom: format(subMonths(now, 12), 'yyyy-MM-dd'), dateTo: format(now, 'yyyy-MM-dd') };
      case 'custom':
        return { dateFrom: customDateFrom || undefined, dateTo: customDateTo || undefined };
      default:
        return { dateFrom: format(subMonths(now, 3), 'yyyy-MM-dd'), dateTo: format(now, 'yyyy-MM-dd') };
    }
  };

  const filters: DepartmentCostFilters = getDateRange();
  const { isLoading, kpis, departmentRows, monthlyTrends, categoryBreakdown, departmentNames } = useDepartmentCostReport(filters);

  // Budget vs Actual chart data
  const budgetVsActualData = useMemo(() =>
    departmentRows
      .filter(r => r.budgetLimit > 0 || r.actualSpend > 0)
      .slice(0, 10)
      .map(r => ({
        name: r.departmentName.length > 12 ? r.departmentName.slice(0, 12) + '…' : r.departmentName,
        fullName: r.departmentName,
        budget: r.budgetLimit,
        actual: r.actualSpend,
      })),
    [departmentRows]
  );

  // Department ranking (horizontal bar)
  const rankingData = useMemo(() =>
    departmentRows
      .filter(r => r.actualSpend > 0)
      .slice(0, 10)
      .map(r => ({
        name: r.departmentName.length > 15 ? r.departmentName.slice(0, 15) + '…' : r.departmentName,
        fullName: r.departmentName,
        spend: r.actualSpend,
      })),
    [departmentRows]
  );

  // Pie chart data
  const pieData = useMemo(() =>
    departmentRows
      .filter(r => r.actualSpend > 0)
      .map(r => ({ name: r.departmentName, value: r.actualSpend })),
    [departmentRows]
  );

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary
    const summaryData = [
      ['Department Cost Analysis Report'],
      [`Generated: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`],
      [`Period: ${filters.dateFrom || 'All'} to ${filters.dateTo || 'Present'}`],
      [],
      ['Department', 'Code', 'Budget', 'Actual Spend', 'Variance', 'Utilization %', 'Transactions', 'Top Categories'],
    ];
    departmentRows.forEach(r => {
      summaryData.push([
        r.departmentName,
        r.departmentCode,
        r.budgetLimit as any,
        r.actualSpend as any,
        r.variance as any,
        r.variancePercent as any,
        r.transactionCount as any,
        r.topCategories.map(c => `${c.name}: ${formatCurrency(c.amount)}`).join('; '),
      ]);
    });
    const ws = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(wb, ws, 'Department Costs');

    // Category breakdown
    const catData = [['Department', 'Category', 'Amount']];
    categoryBreakdown.forEach(c => catData.push([c.departmentName, c.categoryName, c.amount as any]));
    const ws2 = XLSX.utils.aoa_to_sheet(catData);
    XLSX.utils.book_append_sheet(wb, ws2, 'Category Breakdown');

    XLSX.writeFile(wb, `department_cost_analysis_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i}><CardContent className="pt-6"><Skeleton className="h-20 w-full" /></CardContent></Card>
          ))}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <Label className="text-xs text-muted-foreground">Time Range</Label>
              <Select value={timeRange} onValueChange={(v: TimeRange) => setTimeRange(v)}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="this_month">This Month</SelectItem>
                  <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                  <SelectItem value="last_6_months">Last 6 Months</SelectItem>
                  <SelectItem value="last_year">Last Year</SelectItem>
                  <SelectItem value="custom">Custom Range</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {timeRange === 'custom' && (
              <>
                <div>
                  <Label className="text-xs text-muted-foreground">From</Label>
                  <Input type="date" value={customDateFrom} onChange={e => setCustomDateFrom(e.target.value)} className="w-[160px]" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">To</Label>
                  <Input type="date" value={customDateTo} onChange={e => setCustomDateTo(e.target.value)} className="w-[160px]" />
                </div>
              </>
            )}
            <Button variant="outline" size="sm" onClick={exportToExcel} className="ml-auto">
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Total Department Spend</p>
              <div className="p-2 rounded-full bg-red-500/10"><DollarSign className="h-4 w-4 text-red-500" /></div>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(kpis.totalSpend)}</p>
            <p className="text-xs text-muted-foreground mt-1">{kpis.totalDepartments} departments</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Highest Spending</p>
              <div className="p-2 rounded-full bg-amber-500/10"><TrendingUp className="h-4 w-4 text-amber-500" /></div>
            </div>
            <p className="text-2xl font-bold">{formatCurrency(kpis.highestSpendAmount)}</p>
            <p className="text-xs text-muted-foreground mt-1">{kpis.highestSpendDepartment}</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Avg Budget Utilization</p>
              <div className="p-2 rounded-full bg-blue-500/10"><Activity className="h-4 w-4 text-blue-500" /></div>
            </div>
            <p className="text-2xl font-bold">{kpis.avgBudgetUtilization}%</p>
            <p className="text-xs text-muted-foreground mt-1">of allocated budget</p>
          </CardContent>
        </Card>
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-muted-foreground">Over Budget</p>
              <div className="p-2 rounded-full bg-destructive/10"><AlertTriangle className="h-4 w-4 text-destructive" /></div>
            </div>
            <p className="text-2xl font-bold">{kpis.departmentsOverBudget}</p>
            <p className="text-xs text-muted-foreground mt-1">departments exceeded budget</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="ranking" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="ranking">Department Ranking</TabsTrigger>
          <TabsTrigger value="budget">Budget vs Actual</TabsTrigger>
          <TabsTrigger value="trends">Monthly Trends</TabsTrigger>
          <TabsTrigger value="share">Cost Share</TabsTrigger>
        </TabsList>

        <TabsContent value="ranking">
          <Card>
            <CardHeader><CardTitle className="text-lg">Department Spend Ranking</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={Math.max(300, rankingData.length * 45)}>
                <BarChart data={rankingData} layout="vertical" margin={{ left: 20, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tickFormatter={v => formatCurrency(v)} />
                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} labelFormatter={(_, p) => p?.[0]?.payload?.fullName || ''} />
                  <Bar dataKey="spend" fill="hsl(var(--primary))" name="Actual Spend" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="budget">
          <Card>
            <CardHeader><CardTitle className="text-lg">Budget vs Actual Comparison</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={budgetVsActualData} margin={{ left: 20, right: 30, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-35} textAnchor="end" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => formatCurrency(v)} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} labelFormatter={(_, p) => p?.[0]?.payload?.fullName || ''} />
                  <Legend />
                  <Bar dataKey="budget" fill="hsl(var(--muted-foreground))" name="Budget" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="actual" fill="hsl(var(--primary))" name="Actual" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends">
          <Card>
            <CardHeader><CardTitle className="text-lg">Monthly Spend by Department</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={monthlyTrends} margin={{ left: 20, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis tickFormatter={v => formatCurrency(v)} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Legend />
                  {departmentNames.slice(0, 8).map((name, i) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="share">
          <Card>
            <CardHeader><CardTitle className="text-lg">Cost Distribution by Department</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    outerRadius={150}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Department Cost Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Department</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="text-right">Budget</TableHead>
                  <TableHead className="text-right">Actual Spend</TableHead>
                  <TableHead className="text-right">Variance</TableHead>
                  <TableHead className="text-right">Utilization</TableHead>
                  <TableHead className="text-right">Txns</TableHead>
                  <TableHead>Top Categories</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {departmentRows.map(row => (
                  <TableRow key={row.departmentId}>
                    <TableCell className="font-medium">{row.departmentName}</TableCell>
                    <TableCell className="text-muted-foreground">{row.departmentCode}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatCurrency(row.budgetLimit)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{formatCurrency(row.actualSpend)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${row.variance < 0 ? 'text-destructive' : 'text-green-600'}`}>
                      {formatAccounting(row.variance)}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.budgetLimit > 0 ? (
                        <Badge variant={row.variancePercent > 100 ? 'destructive' : row.variancePercent > 80 ? 'secondary' : 'default'}>
                          {row.variancePercent.toFixed(0)}%
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">No budget</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{row.transactionCount}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {row.topCategories.map(c => (
                          <Badge key={c.name} variant="outline" className="text-xs">
                            {c.name}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {departmentRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      No department cost data for the selected period
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DepartmentCostReport;
